import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, setSession, SSO_ONLY } from '../auth/authMode';

function deskSignedInAccount() {
    if (window.self === window.top) return Promise.resolve('');
    return new Promise((resolve) => {
        const timer = window.setTimeout(() => {
            window.removeEventListener('message', onMessage);
            resolve('');
        }, 400);
        function onMessage(event) {
            if (event.data?.type !== 'desk-account') return;
            window.removeEventListener('message', onMessage);
            window.clearTimeout(timer);
            const upn = typeof event.data.upn === 'string' ? event.data.upn.trim() : '';
            resolve(upn.includes('@') ? upn : '');
        }
        window.addEventListener('message', onMessage);
        window.parent.postMessage({ type: 'desk-account-request' }, '*');
    });
}

let cachedDeskAccount = '';

function waitForDeskPoll(poll) {
    return new Promise((resolve, reject) => {
        let tries = 0;
        const timer = window.setInterval(async () => {
            tries += 1;
            if (tries > 90) {
                window.clearInterval(timer);
                reject(new Error('Microsoft sign-in timed out. Try again.'));
                return;
            }
            try {
                const res = await fetch(`${API_URL}/auth/sso/desk-poll/${poll}`);
                const data = await res.json().catch(() => ({}));
                if (data?.code) {
                    window.clearInterval(timer);
                    resolve(data.code);
                }
            } catch {
                /* keep waiting while Microsoft is still open */
            }
        }, 1000);
    });
}

const Login = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('admin@zasya.local');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [loading, setLoading] = useState(false);
    const [ssoWaiting, setSsoWaiting] = useState(false);
    const [mode, setMode] = useState('login'); // login | forgot

    useEffect(() => {
        deskSignedInAccount().then((upn) => {
            cachedDeskAccount = upn;
        });
    }, []);

    const startMicrosoftSignIn = async () => {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        const poll = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        const url = new URL(`${API_URL}/auth/sso/login`);
        if (cachedDeskAccount) url.searchParams.set('login_hint', cachedDeskAccount);
        url.searchParams.set('desk_poll', poll);
        const target = url.toString();
        const popup = window.open(target, 'zasya-desk-auth', 'popup,width=520,height=780');
        // Never navigate this frame to Microsoft — that can blank Desk when the popup fails.
        if (!popup || popup.closed) {
            setError('Could not open the Microsoft sign-in window. Allow popups for HR, then try again.');
            return;
        }
        setError('');
        setInfo('Continue in the sign-in window. Desk stays open.');
        setSsoWaiting(true);
        try {
            const code = await waitForDeskPoll(poll);
            const res = await fetch(`${API_URL}/auth/sso/microsoft/exchange`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Microsoft sign-in failed');
            setSession(data.token, data.user);
            navigate('/dashboard', { replace: true });
        } catch (err) {
            setError(err.message || 'Microsoft sign-in failed');
        } finally {
            setSsoWaiting(false);
            try { popup.close(); } catch { /* popup may already be closed */ }
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setInfo('');
        setLoading(true);
        try {
            if (mode === 'forgot') {
                const res = await fetch(`${API_URL}/auth/forgot-password`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email }),
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data.error || 'Request failed');
                setInfo(data.message || 'Check your email for a reset link.');
                return;
            }
            const res = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.error || 'Login failed');
            }
            setSession(data.token, data.user);
            navigate('/dashboard', { replace: true });
        } catch (err) {
            setError(err.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f766e 100%)',
            fontFamily: 'Georgia, "Times New Roman", serif',
            padding: 24,
        }}>
            <form
                onSubmit={onSubmit}
                style={{
                    width: '100%',
                    maxWidth: 400,
                    background: 'rgba(255,255,255,0.96)',
                    padding: '36px 32px',
                    borderRadius: 4,
                    boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
                }}
            >
                <p style={{ margin: 0, letterSpacing: '0.12em', fontSize: 12, color: '#0f766e', textTransform: 'uppercase' }}>
                    ZasyaHR
                </p>
                <h1 style={{ margin: '8px 0 8px', fontSize: 28, color: '#0f172a' }}>
                    {mode === 'forgot' ? 'Forgot password' : 'Sign in'}
                </h1>
                <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 14, fontFamily: 'system-ui, sans-serif' }}>
                    Use your work Microsoft account. HR is open to every employee.
                </p>
                <button
                    type="button"
                    onClick={() => { void startMicrosoftSignIn(); }}
                    disabled={ssoWaiting}
                    style={{
                        width: '100%',
                        marginBottom: 18,
                        padding: '10px 14px',
                        borderRadius: 4,
                        border: '1px solid #0f766e',
                        background: '#0f766e',
                        color: '#fff',
                        fontFamily: 'system-ui, sans-serif',
                        cursor: 'pointer',
                    }}
                >
                    {ssoWaiting ? 'Waiting for Microsoft…' : 'Sign in with Microsoft'}
                </button>
                {SSO_ONLY ? (
                    error ? (
                        <p style={{ color: '#b91c1c', fontSize: 13, fontFamily: 'system-ui, sans-serif' }}>{error}</p>
                    ) : null
                ) : (
                <>
                <label style={{ display: 'block', fontSize: 13, color: '#334155', fontFamily: 'system-ui, sans-serif' }}>
                    Email
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        style={inputStyle}
                    />
                </label>
                {mode === 'login' && (
                    <label style={{ display: 'block', marginTop: 14, fontSize: 13, color: '#334155', fontFamily: 'system-ui, sans-serif' }}>
                        Password
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            style={inputStyle}
                        />
                    </label>
                )}

                {error ? (
                    <p style={{ color: '#b91c1c', fontSize: 13, fontFamily: 'system-ui, sans-serif' }}>{error}</p>
                ) : null}
                {info ? (
                    <p style={{ color: '#166534', fontSize: 13, fontFamily: 'system-ui, sans-serif' }}>{info}</p>
                ) : null}

                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        marginTop: 20,
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: '#0f766e',
                        color: '#fff',
                        fontSize: 15,
                        cursor: loading ? 'wait' : 'pointer',
                        fontFamily: 'system-ui, sans-serif',
                    }}
                >
                    {loading
                        ? 'Please wait…'
                        : (mode === 'forgot' ? 'Send reset link' : 'Sign in')}
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setMode(mode === 'forgot' ? 'login' : 'forgot');
                        setError('');
                        setInfo('');
                    }}
                    style={{
                        marginTop: 12,
                        width: '100%',
                        padding: '8px',
                        border: 'none',
                        background: 'transparent',
                        color: '#0f766e',
                        fontSize: 13,
                        cursor: 'pointer',
                        fontFamily: 'system-ui, sans-serif',
                    }}
                >
                    {mode === 'forgot' ? 'Back to sign in' : 'Forgot password?'}
                </button>
                </>
                )}
            </form>
        </div>
    );
};

const inputStyle = {
    display: 'block',
    width: '100%',
    marginTop: 6,
    padding: '10px 12px',
    border: '1px solid #cbd5e1',
    borderRadius: 2,
    fontSize: 14,
    boxSizing: 'border-box',
    fontFamily: 'system-ui, sans-serif',
};

export default Login;
